import { ShadcnButton } from "@components/items/display/ShadcnButton";
import TranslatedText from "@components/items/display/TranslatedText";
import { signInWithPhoneNumber, useSignInWithPhoneNumber } from "@lib/stores";
import { Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import Countdown from "react-countdown";
import VerificationInput from "react-verification-input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@ui/dialog";

interface PhoneNumberCodeFormProps {
  phoneNumber: string;
  open: boolean;
  setEditState: React.Dispatch<React.SetStateAction<boolean>>;
  onCancel: () => void;
  onCodeResend: (onSuccess?: () => void) => void;
  isCodeResendLoading?: boolean;
}

const COUNTDOWN = 5 * 60 * 1000;
const CODE_LENGTH = 6;

const PhoneNumberCodeForm = ({
  phoneNumber,
  open,
  setEditState,
  onCancel,
  onCodeResend,
  isCodeResendLoading,
}: PhoneNumberCodeFormProps) => {
  const countdownRef = useRef<Countdown>(null);

  const [code, setCode] = useState("");
  const [validateCode, setValidateCode] = useState(false);
  const [countdown, setCountdown] = useState(Date.now() + COUNTDOWN);

  const { status } = useSignInWithPhoneNumber();

  const twoDigits = (number: number) => {
    return number.toLocaleString("en-US", {
      minimumIntegerDigits: 2,
      useGrouping: false,
    });
  };

  const onSubmit = async () => {
    if (!code || code.length < CODE_LENGTH) {
      setValidateCode(true);
      return;
    }

    signInWithPhoneNumber({
      code,
      phoneNumber,
    });
    setEditState(false);
  };

  useEffect(() => {
    const cssTemplateString = `
      .vi__character--selected {
        position: relative;
      }

      .vi__character--selected:not(.vi__character--filled) {
        color: transparent;
      }

      .vi__character--selected:not(.vi__character--filled)::after {
        content: "";
        width: 1px;
        height: 50%;
        background-color: black;
        display: block;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        animation: blink 1s infinite step-end;
      }

      @keyframes blink {
        0% {
          opacity: 1;
        }
        50% {
          opacity: 0;
        }
      }
    `;

    const styleTag = document.createElement("style");

    styleTag.innerHTML = cssTemplateString;

    document.head.insertAdjacentElement("beforeend", styleTag);
  }, []);

  useEffect(() => {
    if (countdownRef.current) {
      countdownRef.current.api?.start();
    }
  }, [countdown]);

  return (
    <Dialog open={open}>
      <DialogContent className="z-50 bg-white sm:max-w-[425px] dark:bg-[#37383F]">
        <DialogHeader>
          <DialogTitle
            className="px-9 text-center text-[22px] font-medium"
            dir="ltr"
          >
            +{phoneNumber}
          </DialogTitle>
          <DialogDescription className="mt-5 px-9 text-center text-[18px]">
            <TranslatedText text="onboarding.titles.we have sent an sms containing the code to the phone number above" />
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2">
          <Countdown
            ref={countdownRef}
            autoStart={true}
            date={countdown}
            renderer={({ seconds, minutes, completed }) => {
              if (completed) {
                return (
                  <div
                    className="flex cursor-pointer items-center justify-center text-center text-[14px] text-[#898A8D]"
                    style={{ WebkitTapHighlightColor: "transparent" }}
                  >
                    <TranslatedText
                      text={`onboarding.titles.${"you didn't get the code?"}`}
                    />
                    <div
                      className="mr-1 flex items-center justify-center gap-2 font-medium text-primary"
                      onClick={() => {
                        onCodeResend(() => {
                          setCountdown(Date.now() + COUNTDOWN);
                        });
                      }}
                      style={{ WebkitTapHighlightColor: "transparent" }}
                    >
                      <TranslatedText text="onboarding.buttons.resend" />
                      {isCodeResendLoading && (
                        <Loader2 className={`animate-spin`} />
                      )}
                    </div>
                  </div>
                );
              } else {
                return (
                  <div className="flex w-full items-center justify-center gap-2 text-[14px] text-[#898A8D]">
                    <TranslatedText text="onboarding.titles.the code becomes invalid in" />
                    <div>
                      <div className="flex items-start justify-start">
                        <div>
                          <div className="">{twoDigits(seconds)}</div>
                        </div>
                        <div className="">:</div>
                        <div>
                          <div className="">{twoDigits(minutes)}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }
            }}
          />
        </div>
        <form className="mx-auto mt-3 flex w-full flex-col justify-center space-y-2 px-4 sm:w-[375px] sm:px-0">
          <div className="relative mx-auto w-fit sm:w-full">
            <div className="flex w-full items-center justify-center" dir="ltr">
              <VerificationInput
                length={CODE_LENGTH}
                validChars="0-9"
                inputProps={{ type: "tel" }}
                placeholder=""
                classNames={{
                  container:
                    "w-[260px] h-fit gap-1 border-none outline-none focus:outline-none focus:ring-0",
                  character: `w-[40px] h-[42px] flex justify-center items-center ${
                    validateCode ? "border border-[#E95252]" : "border-none"
                  } rounded-[7px] bg-[#D9D9D9] text-black text-[16px] sm:text-[18px]`,
                  characterInactive: "rounded-[7px] bg-[#D9D9D9] text-black",
                  characterSelected:
                    "outline-none rounded-[7px] bg-[#D9D9D9] text-black",
                  characterFilled: "rounded-[7px] bg-[#D9D9D9] text-black",
                }}
                value={code}
                onChange={(value) => {
                  setCode(value);
                }}
              />
            </div>
            {validateCode && (
              <div className="absolute right-0 top-[50px] text-center text-[12px] text-[#E95252]">
                <TranslatedText text="onboarding.inputs.errors.the entered code is incorrect" />
              </div>
            )}
          </div>
        </form>

        <DialogFooter className="mt-5 flex w-full items-center justify-center gap-5 sm:justify-center">
          <ShadcnButton
            className="bg-transparent hover:bg-transparent/20"
            onClick={onCancel}
          >
            <TranslatedText
              text={`onboarding.buttons.${"cancel"}`}
              className="text-[16px] font-normal leading-[22px] dark:text-white"
            />
          </ShadcnButton>
          <ShadcnButton
            disabled={code.length < CODE_LENGTH || status === "loading"}
            className="px-6 text-white"
            onClick={onSubmit}
            isLoading={status === "loading"}
            type="button"
          >
            <TranslatedText
              text={`onboarding.buttons.${"continue"}`}
              className="text-[16px] font-normal leading-[22px]"
            />
          </ShadcnButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PhoneNumberCodeForm;
